<template>
  <teleport to="body" :disabled="!isMounted">
    <div
      v-if="renderIfHidden || isOpen"
      v-show="isOpen"
      class="modal-overlay z-1002"
      @click="close"
    >
      <div
        ref="dialogRef"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="props.variant === 'default' ? titleId : undefined"
        :aria-label="props.variant === 'map' ? t('dialog_aria_label') : undefined"
        class="max-h-full p-0 md:p-12 grid grid-cols-1 place-content-center"
        @keydown.escape.prevent="close"
      >
        <div
          :class="['modal bg-white overflow-hidden w-screen md:h-auto max-h-full max-w-full', props.width, { fullscreen: props.fullscreen, 'md:rounded-md': props.variant === 'default' } ]"
          :data-datepicker="props.isDaterangePicker || undefined"
          @click.stop
        >
          <!-- Close button for map variant -->
          <button
            v-if="props.variant === 'map'"
            ref="closeButton"
            type="button"
            :aria-label="t('close_aria_label')"
            class="cursor-pointer absolute top-4 right-4 z-1003 bg-white ring-3 ring-offset-0 ring-neutral-900/20 w-9 h-9 flex items-center justify-center rounded-sm"
            @click="close"
          >
            <UiIcon
              src="/svg/icons/cancel.svg"
              width="28"
              height="28"
              class="fill-neutral-500 rotate-180"
              aria-hidden="true"
            />
          </button>

          <!-- Default variant with header -->
          <div v-if="props.variant === 'default'" class="relative">
            <button
              ref="closeButton"
              type="button"
              :aria-label="t('close_aria_label')"
              class="cursor-pointer absolute right-0 top-0 h-full flex justify-items-center p-5"
              @click="close"
            >
              <UiIcon
                src="/svg/icons/cancel.svg"
                width="34"
                height="34"
                class="fill-neutral-500 rotate-180 ml-[-3px]"
                aria-hidden="true"
              />
            </button>

            <div
              :id="titleId"
              class="border-b border-neutral-200 text-lg font-bold py-6 px-16 min-h-[70px] text-center"
            >
              <template v-if="$slots.head">
                <slot name="head" />
              </template>
            </div>
          </div>
          <div :class="props.variant === 'default' ? 'px-6 md:px-8 py-8 h-full overflow-auto' : 'h-full overflow-auto'">
            <slot />
          </div>
          <div v-if="$slots.foot && props.variant === 'default'" class="sticky bg-white bottom-0">
            <slot name="foot" />
          </div>
          <div
            v-if="$slots.footerClose && props.variant === 'default'"
            class="sticky bg-white bottom-0"
            @click="close"
          >
            <slot name="footerClose" :close="close" />
          </div>
        </div>
      </div>
    </div>
  </teleport>
  <slot name="trigger" />
</template>

<script setup lang="ts">
import { useModalWindowStore } from '~~/stores/modalWindow'

const emit = defineEmits(['shown', 'hidden'])

const modalWindowStore = useModalWindowStore()

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  width: {
    type: String,
    default: 'md:w-160'
  },
  fullscreen: {
    type: Boolean,
    default: false
  },
  renderIfHidden: {
    type: Boolean,
    default: true
  },
  modalFocus: {
    type: Boolean,
    default: true
  },
  isDaterangePicker: {
    type: Boolean,
    default: false
  },
  modalId: {
    type: String,
    required: true
  },
  closeWithHistoryBack: {
    type: Boolean,
    default: true
  },
  variant: {
    type: String as PropType<'default' | 'map'>,
    default: 'default',
    validator: (value: string) => ['default', 'map'].includes(value)
  }
})

const { translate: t } = useTranslations('UiModal')
const titleId = `modal-title-${props.modalId}`

const isOpen = ref(props.open)
const closeButton = ref<HTMLElement | null>(null)
const dialogRef = ref<HTMLElement | null>(null)
const isMounted = ref(false)
const focusTrap = useFocusTrap(dialogRef)

const show = () => {
  isOpen.value = true

  // Register modal in store
  modalWindowStore.registerModal(props.modalId, { modalOpen: true, modalId: props.modalId })

  // Only add body class for first modal AND when component is mounted
  if (isMounted.value && modalWindowStore.isFirstModal) {
    document.body.classList.add('modal-open')
  }

  // Push history state for each modal
  if (import.meta.client) {
    window.history.pushState({ modalOpen: true, modalId: props.modalId }, '')
  }

  // Focus management
  if (props.modalFocus) {
    nextTick(() => {
      focusTrap.activate({ initialFocus: closeButton.value })
    })
  }

  emit('shown')
}

const hide = (reason?: string) => {
  focusTrap.deactivate()

  // Unregister modal from store
  modalWindowStore.unregisterModal(props.modalId)

  isOpen.value = false

  // Only remove body class when last modal closes
  if (modalWindowStore.isLastModal) {
    document.body.classList.remove('modal-open')
  }

  emit('hidden', reason)
}

const closeAndNavigateBack = () => {
  hide()
  if (import.meta.client) {
    window.history.back()
  }
}

const close = () => {
  if (props.closeWithHistoryBack) {
    closeAndNavigateBack()
  } else {
    hide()
  }
}

watch(() => props.open, (newValue) => {
  isOpen.value = newValue
  if (isOpen.value) {
    show()
  } else {
    hide()
  }
})

// Popstate handler for browser back button
if (import.meta.client) {
  const handlePopstate = () => {
    // Only close this modal if it's the topmost one in the stack
    const topModal = modalWindowStore.getTopModal()
    if (topModal?.id === props.modalId) {
      hide('popstate')
    }
  }

  onMounted(() => {
    window.addEventListener('popstate', handlePopstate)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('popstate', handlePopstate)

    // Cleanup: ensure modal is removed from store if component is destroyed while open
    if (isOpen.value) {
      focusTrap.deactivate()
      modalWindowStore.unregisterModal(props.modalId)
      if (modalWindowStore.isLastModal) {
        document.body.classList.remove('modal-open')
      }
    }
  })
}

// Set isMounted flag when component is mounted on client
onMounted(() => {
  isMounted.value = true

  // If modal should be open at mount, ensure body class is set
  if (isOpen.value && modalWindowStore.isFirstModal) {
    document.body.classList.add('modal-open')
  }
})

defineExpose({
  show,
  hide,
  close,
  isOpen
})
</script>

<style>
@reference "~/assets/css/main.css";
body.modal-open{
  @apply overflow-hidden;
}
.modal-overlay {
  @apply fixed inset-x-0 inset-y-0;
  display: flex;
  justify-content: center;
  background-color: #000000da;
  .modal{
    @apply fixed md:relative inset-x-0 inset-y-0 md:inset-x-auto md:inset-y-auto flex flex-col;
  }
}
.fullscreen {
  width: 100vw;
  height: 100vh;
  display: flex !important;
  flex-direction: column;
}
</style>
